import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Mail, Bell, Plus, Pencil, Trash2, PlayCircle, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REMINDER_SOURCES = [
  { value: "training_due", label: "Training due" },
  { value: "cert_expiry", label: "Certification expiry" },
  { value: "h_s_review", label: "Health & Safety review" },
  { value: "foreign_worker_permit", label: "Foreign worker permit" },
  { value: "lmia_expiry", label: "LMIA expiry" },
  { value: "equipment_maintenance", label: "Equipment maintenance" },
  { value: "vehicle_inspection", label: "Vehicle inspection" },
  { value: "membership_renewal", label: "Membership renewal" },
  { value: "custom", label: "Custom" },
];

const blankRule = () => ({
  name: "", source: "training_due",
  lead_days: [60, 30, 7, 0], notify_roles: ["admin"],
  via_email: false, via_portal: true,
  subject_template: "", body_template: "", is_active: true, notes: "",
});

export default function NotificationSettings() {
  const [tab, setTab] = useState("auto-emails");
  const [emails, setEmails] = useState([]);
  const [rules, setRules] = useState([]);
  const [editEmail, setEditEmail] = useState(null);
  const [editRule, setEditRule] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dryRun, setDryRun] = useState(null);

  const load = async () => {
    try {
      const [e, r] = await Promise.all([
        axios.get(`${API}/auto-emails`),
        axios.get(`${API}/reminder-rules?include_inactive=true`),
      ]);
      setEmails(e.data || []);
      setRules(r.data || []);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const saveEmail = async () => {
    try { await axios.put(`${API}/auto-emails`, editEmail); toast.success("Saved"); setEditEmail(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const toggleEmail = async (e, val) => {
    try { await axios.put(`${API}/auto-emails`, { ...e, is_enabled: val }); load(); }
    catch (err) { toast.error("Toggle failed"); }
  };

  const previewEmail = async () => {
    try {
      const r = await axios.post(`${API}/auto-emails/preview`, {
        subject_template: editEmail.subject_template || "", body_template: editEmail.body_template || "",
        vars: { client_name: "Jane Doe", job_title: "Lawn cut", date: "Saturday 2pm" },
      });
      setPreview(r.data);
    } catch { toast.error("Preview failed"); }
  };

  const saveRule = async () => {
    if (!editRule.name) { toast.error("Name required"); return; }
    try {
      if (editRule.id) await axios.put(`${API}/reminder-rules/${editRule.id}`, editRule);
      else await axios.post(`${API}/reminder-rules`, editRule);
      toast.success("Saved"); setEditRule(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteRule = async (id) => {
    if (!window.confirm("Deactivate rule?")) return;
    try { await axios.delete(`${API}/reminder-rules/${id}`); load(); } catch { toast.error("Failed"); }
  };
  const doDryRun = async (rid) => {
    try { const r = await axios.post(`${API}/reminder-rules/${rid}/dry-run`); setDryRun(r.data); }
    catch (e) { toast.error("Dry-run failed"); }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="notification-settings-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><Bell className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Notification Settings</h1>
          <p className="text-muted-foreground">Auto-email toggles + custom reminder rules.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="auto-emails" data-testid="tab-auto-emails"><Mail className="h-4 w-4 mr-1" />Auto-Emails</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules"><Bell className="h-4 w-4 mr-1" />Reminder rules</TabsTrigger>
        </TabsList>

        <TabsContent value="auto-emails">
          <Card>
            <CardHeader><CardTitle>Auto-email triggers</CardTitle><CardDescription>OFF by default. Each event has its own toggle and template.</CardDescription></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Event</TableHead><TableHead>Audience</TableHead><TableHead>Lead time</TableHead><TableHead>Status</TableHead><TableHead className="text-right" />
                </TableRow></TableHeader>
                <TableBody>
                  {emails.map((e) => (
                    <TableRow key={e.event_type} data-testid={`auto-email-row-${e.event_type}`}>
                      <TableCell className="font-medium">{e.event_type.replace(/_/g, " ")}</TableCell>
                      <TableCell><Badge variant="outline">{e.audience}</Badge></TableCell>
                      <TableCell className="text-xs">{e.lead_minutes ? `${e.lead_minutes} min` : "—"}</TableCell>
                      <TableCell>
                        <Switch checked={e.is_enabled} onCheckedChange={(v) => toggleEmail(e, v)} data-testid={`toggle-${e.event_type}`} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => setEditEmail({ ...e })} data-testid={`edit-${e.event_type}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules">
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <div><CardTitle>{rules.length} reminder rules</CardTitle><CardDescription>Multi-stage lead-time rules across operational sources.</CardDescription></div>
              <Button onClick={() => setEditRule(blankRule())} data-testid="new-rule-btn"><Plus className="h-4 w-4 mr-1" /> New rule</Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Source</TableHead><TableHead>Lead days</TableHead><TableHead>Channels</TableHead><TableHead>Active</TableHead><TableHead className="text-right" />
                </TableRow></TableHeader>
                <TableBody>
                  {rules.map((r) => (
                    <TableRow key={r.id} className={!r.is_active ? "opacity-50" : ""} data-testid={`rule-row-${r.id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-xs">{r.source}</TableCell>
                      <TableCell className="text-xs">{r.lead_days.join(", ")}</TableCell>
                      <TableCell className="text-xs">{r.via_portal ? "📨 portal" : ""} {r.via_email ? "✉️ email" : ""}</TableCell>
                      <TableCell><Badge variant={r.is_active ? "default" : "outline"}>{r.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => doDryRun(r.id)} data-testid={`dry-run-${r.id}`}><PlayCircle className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditRule({ ...r })}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteRule(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rules.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No reminder rules yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Email editor */}
      <Dialog open={!!editEmail} onOpenChange={(o) => !o && setEditEmail(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Edit: {editEmail?.event_type?.replace(/_/g, " ")}</DialogTitle><DialogDescription>Variables: {"{client_name} {job_title} {date}"} etc.</DialogDescription></DialogHeader>
          {editEmail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={editEmail.is_enabled} onCheckedChange={(v) => setEditEmail({ ...editEmail, is_enabled: v })} /> Enabled</label>
                <div>
                  <Label>Audience</Label>
                  <Select value={editEmail.audience} onValueChange={(v) => setEditEmail({ ...editEmail, audience: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Subject template</Label><Input value={editEmail.subject_template || ""} onChange={(e) => setEditEmail({ ...editEmail, subject_template: e.target.value })} data-testid="subject-input" /></div>
              <div><Label>Body template</Label><Textarea rows={5} value={editEmail.body_template || ""} onChange={(e) => setEditEmail({ ...editEmail, body_template: e.target.value })} data-testid="body-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Lead time (minutes)</Label><Input type="number" value={editEmail.lead_minutes} onChange={(e) => setEditEmail({ ...editEmail, lead_minutes: parseInt(e.target.value || 0) })} /></div>
                <label className="flex items-center gap-2 cursor-pointer pt-6"><Switch checked={editEmail.cc_admin} onCheckedChange={(v) => setEditEmail({ ...editEmail, cc_admin: v })} /> CC admin</label>
              </div>
              <Button variant="outline" onClick={previewEmail} data-testid="preview-btn"><Eye className="h-4 w-4 mr-1" />Preview rendered</Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmail(null)}>Cancel</Button>
            <Button onClick={saveEmail} data-testid="save-email-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Preview</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Subject:</p>
              <p className="font-medium border rounded p-2">{preview.subject}</p>
              <p className="text-xs text-muted-foreground mt-3">Body:</p>
              <p className="whitespace-pre-wrap border rounded p-2 text-sm">{preview.body}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rule editor */}
      <Dialog open={!!editRule} onOpenChange={(o) => !o && setEditRule(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editRule?.id ? "Edit rule" : "New rule"}</DialogTitle></DialogHeader>
          {editRule && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={editRule.name} onChange={(e) => setEditRule({ ...editRule, name: e.target.value })} data-testid="rule-name-input" /></div>
                <div>
                  <Label>Source</Label>
                  <Select value={editRule.source} onValueChange={(v) => setEditRule({ ...editRule, source: v })}>
                    <SelectTrigger data-testid="rule-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{REMINDER_SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Lead days (csv, e.g. 60,30,7,0)</Label>
                <Input value={editRule.lead_days.join(",")} onChange={(e) => setEditRule({ ...editRule, lead_days: e.target.value.split(",").map((x) => parseInt(x.trim())).filter((x) => !isNaN(x)) })} data-testid="lead-days-input" />
              </div>
              <div>
                <Label>Notify roles (csv: admin/employee/client/pm)</Label>
                <Input value={editRule.notify_roles.join(",")} onChange={(e) => setEditRule({ ...editRule, notify_roles: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid="roles-input" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={editRule.via_portal} onCheckedChange={(v) => setEditRule({ ...editRule, via_portal: v })} data-testid="via-portal-switch" /> Portal bell</label>
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={editRule.via_email} onCheckedChange={(v) => setEditRule({ ...editRule, via_email: v })} /> Email</label>
              </div>
              <div><Label>Subject template</Label><Input value={editRule.subject_template || ""} onChange={(e) => setEditRule({ ...editRule, subject_template: e.target.value })} /></div>
              <div><Label>Body template</Label><Textarea rows={3} value={editRule.body_template || ""} onChange={(e) => setEditRule({ ...editRule, body_template: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRule(null)}>Cancel</Button>
            <Button onClick={saveRule} data-testid="save-rule-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dry-run dialog */}
      <Dialog open={!!dryRun} onOpenChange={(o) => !o && setDryRun(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Dry-run preview</DialogTitle><DialogDescription>{dryRun?.would_fire_count} reminders would fire today.</DialogDescription></DialogHeader>
          {dryRun && (
            <div className="space-y-2 max-h-[60vh] overflow-auto">
              {dryRun.matches.map((m, i) => (
                <div key={i} className="border rounded p-2 text-sm">
                  <p className="font-medium">{m.label}</p>
                  <p className="text-xs text-muted-foreground">{m.due || m.expiry} · lead day {m.lead_day}</p>
                </div>
              ))}
              {dryRun.matches.length === 0 && <p className="text-muted-foreground py-4 text-center">No matches.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
